<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { NButton, NDrawer, NDrawerContent, NEmpty } from 'naive-ui'
import RouteShell from './RouteShell.vue'
import ChannelList from '../components/channels/ChannelList.vue'
import ChannelUnreadBadge from '../components/channels/ChannelUnreadBadge.vue'
import ChannelHeader from '../components/channels/ChannelHeader.vue'
import ChannelFeed from '../components/channels/ChannelFeed.vue'
import ChannelPostBox from '../components/channels/ChannelPostBox.vue'
import ChannelMembersPanel from '../components/channels/ChannelMembersPanel.vue'
import ChannelFormModal from '../components/channels/ChannelFormModal.vue'
import ChannelDeleteModal from '../components/channels/ChannelDeleteModal.vue'
import { useAgentChannelsStore } from '../stores/agent_channels'
import { useAgentStore } from '../stores/agent'
import { useChannelActions } from '../composables/useChannelActions'
import { useDocumentTitle } from '../composables/useDocumentTitle'
import { useIsMobile } from '../composables/useIsMobile'

const store = useAgentChannelsStore()
const agent = useAgentStore()
const actions = useChannelActions()
const isMobile = useIsMobile()
const route = useRoute()
useDocumentTitle('Agent channels')

const DRAWER_WIDTH = '80%'

const formMode = ref<'create' | 'rename' | null>(null)
const showDelete = ref(false)
const listOpen = ref(false)
const membersOpen = ref(false)

const selected = computed(() => store.channels.find((c) => c.id === store.selectedId) ?? null)
const sessionNames = computed(() =>
  Object.fromEntries(agent.sessions.flatMap((s) => (s.name ? [[s.thread_id, s.name]] : []))),
)
const listLoadFailed = computed(() => !!store.loadError && store.channels.length === 0)
const formOpen = computed({ get: () => formMode.value !== null, set: (open) => !open && (formMode.value = null) })

onMounted(async () => {
  void agent.fetchSessions()
  await store.fetchChannels()
  const wanted = Number(route.query.channel)
  if (Number.isInteger(wanted) && store.channels.some((c) => c.id === wanted)) await store.select(wanted)
})
onBeforeUnmount(() => store.close())

async function selectChannel(id: number): Promise<void> {
  listOpen.value = false
  await store.select(id)
}

const retry = () => (store.selectedId === null ? store.fetchChannels() : store.select(store.selectedId))

async function submitForm(name: string): Promise<void> {
  const ok = formMode.value === 'create' ? await actions.create(name) : await actions.rename(selected.value!, name)
  if (ok) formMode.value = null
}

async function confirmDelete(): Promise<void> {
  if (await actions.remove(selected.value!)) showDelete.value = false
}
</script>

<template>
  <RouteShell :show-sidebar="!isMobile" :main-scroll="false">
    <template v-if="!isMobile" #sidebar>
      <aside class="w-64 shrink-0 border-r border-white/10 overflow-y-auto px-3 py-4">
        <ChannelList
          :channels="store.channels"
          :selected-id="store.selectedId"
          :loading="!store.channelsLoaded"
          :load-failed="listLoadFailed"
          @select="selectChannel"
          @new="formMode = 'create'"
        >
          <template #unread="{ channel }"
            ><ChannelUnreadBadge :count="channel.unread_count" :channel-id="channel.id"
          /></template>
        </ChannelList>
      </aside>
    </template>

    <div class="h-full min-h-0 flex gap-4">
      <div class="flex-1 min-w-0 flex flex-col gap-3">
        <div v-if="isMobile" class="flex items-center gap-2">
          <NButton size="small" data-testid="channels-toggle" :aria-expanded="listOpen" @click="listOpen = true">
            <span aria-hidden="true">☰</span> Channels
          </NButton>
        </div>

        <template v-if="selected">
          <ChannelHeader
            :channel="selected"
            :show-members-toggle="isMobile"
            @rename="formMode = 'rename'"
            @archive="actions.setArchived(selected, !selected.archived)"
            @delete="showDelete = true"
            @members="membersOpen = true"
          />
          <p v-if="selected.archived" class="m-0 text-sm text-amber-300" data-testid="archived-banner">
            Archived. Read-only; unarchive to post.
          </p>
          <ChannelFeed
            :messages="store.messages"
            :loading="store.loadingMessages"
            :error="store.loadError"
            :session-names="sessionNames"
            @retry="retry"
          />
          <ChannelPostBox :key="selected.id" :channel="selected" />
        </template>

        <template v-else>
          <h1 class="text-xl font-semibold text-white m-0">Agent channels</h1>
          <div v-if="store.loadError" class="flex items-center gap-2 text-sm text-red-300" role="alert">
            <span data-testid="channels-load-error">{{ store.loadError }}</span>
            <NButton size="tiny" data-testid="channels-retry" @click="retry">Retry</NButton>
          </div>
          <NEmpty
            v-else-if="store.channelsLoaded"
            size="large"
            :description="
              store.channels.length === 0
                ? 'Create a channel to let agent threads talk to each other.'
                : 'Pick a channel to read its feed.'
            "
            class="py-12"
            data-testid="no-selection"
          />
        </template>
      </div>

      <aside v-if="selected && !isMobile" class="w-64 shrink-0 border-l border-white/10 pl-4 overflow-y-auto">
        <ChannelMembersPanel
          :key="selected.id"
          :channel="selected"
          :sessions="agent.sessions"
          @add="actions.addMember(selected, $event)"
          @remove="actions.removeMember(selected, $event)"
        />
      </aside>
    </div>

    <NDrawer v-if="isMobile" v-model:show="listOpen" :width="DRAWER_WIDTH" placement="left">
      <NDrawerContent aria-label="Channels" closable>
        <ChannelList
          :channels="store.channels"
          :selected-id="store.selectedId"
          :loading="!store.channelsLoaded"
          :load-failed="listLoadFailed"
          @select="selectChannel"
          @new="((listOpen = false), (formMode = 'create'))"
        >
          <template #unread="{ channel }"
            ><ChannelUnreadBadge :count="channel.unread_count" :channel-id="channel.id"
          /></template>
        </ChannelList>
      </NDrawerContent>
    </NDrawer>
    <NDrawer v-if="isMobile && selected" v-model:show="membersOpen" :width="DRAWER_WIDTH" placement="right">
      <NDrawerContent aria-label="Members" closable>
        <ChannelMembersPanel
          :key="selected.id"
          :channel="selected"
          :sessions="agent.sessions"
          @add="actions.addMember(selected, $event)"
          @remove="actions.removeMember(selected, $event)"
        />
      </NDrawerContent>
    </NDrawer>

    <ChannelFormModal
      v-model:show="formOpen"
      :mode="formMode ?? 'create'"
      :initial-name="selected?.name ?? ''"
      @submit="submitForm"
    />
    <ChannelDeleteModal v-model:show="showDelete" :name="selected?.name ?? ''" @confirm="confirmDelete" />
  </RouteShell>
</template>
